import { useMemo } from 'react'
import { format } from 'date-fns'

import {
  calculateItemMetrics,
  calculateAggregatedMetrics,
  COMMON_METRIC_ORDER,
  processDispositionMetricsForReports,
  processDurationMetricsForReports,
} from '@/utils/metrics'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ReportSingleDataGrid } from './ReportSingleDataGrid'
import type { ReportData } from '@/entities/report'

interface ReportsTableProps {
  report: ReportData
}

function formatDateStr(dateStr: string, formatStr: string): string {
  try {
    return format(new Date(dateStr), formatStr)
  } catch {
    return dateStr
  }
}

export function ReportsTable({ report }: ReportsTableProps) {
  const data = report.data || []
  const grouping = report.grouping || ''
  const listNames = report.listNames

  // Data has metric fields for each type
  const columnKeys = useMemo(() => {
    return data.map((item) => item.groupKey.toString())
  }, [data])

  // Values to show
  const commonMetricOrder = COMMON_METRIC_ORDER
  const commonMetrics = useMemo(() => {
    return [
      {
        groupKey: 'total',
        metricName: 'Total',
        ...calculateAggregatedMetrics(data),
      },
      ...data.map((item) => ({
        groupKey: item.groupKey,
        ...calculateItemMetrics(item),
      })),
    ]
  }, [data])

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Custom Report</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground">
          <p>No report data available. Please modify your search parameters.</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-semibold">Custom Report</CardTitle>
        <CardDescription className="text-muted-foreground">
          Generated custom report for campaign data from {formatDateStr(report.startDateStr, 'MMM dd, yyyy')} to{' '}
          {formatDateStr(report.endDateStr, 'MMM dd, yyyy')}, split by{' '}
          <span className="font-medium text-foreground">{grouping.charAt(0).toUpperCase() + grouping.slice(1)}</span>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-4">
          {/* Common metrics */}
          <ReportSingleDataGrid
            columnData={commonMetrics}
            columnKeys={columnKeys}
            metricOrder={commonMetricOrder}
            listNames={listNames}
            lastColumn={{
              field: 'total',
              headerName: 'Total',
              type: 'number' as const,
            }}
          />

          {/* Connected Dispositions - % of connected calls */}
          <ReportSingleDataGrid
            title="Connected Dispositions (% of Connected Calls)"
            columnData={processDispositionMetricsForReports(data, columnKeys).connectedDispositionsData}
            columnKeys={columnKeys}
            listNames={listNames}
          />

          {/* Unconnected Dispositions - % of total */}
          <ReportSingleDataGrid
            title="Unconnected Dispositions (% of Total)"
            columnData={processDispositionMetricsForReports(data, columnKeys).unconnectedDispositionsData}
            columnKeys={columnKeys}
            listNames={listNames}
          />

          {/* Durations */}
          <ReportSingleDataGrid
            title="Duration Metrics (%)"
            columnData={processDurationMetricsForReports(data)}
            columnKeys={columnKeys}
            listNames={listNames}
          />
        </div>
      </CardContent>
    </Card>
  )
}